import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  GraduationCap,
  ArrowRight,
  Check,
  BarChart3,
  Target,
  Trophy,
  Calendar,
  BookOpen,
  Award,
  Star,
} from "lucide-react";
import parentChildImg from "@/assets/parent-child.png";
import teacherStudentImg from "@/assets/teacher-student.png";

type JourneyType = "parent" | "teacher" | null;

export default function UserJourneySelector() {
  const [selectedJourney, setSelectedJourney] = useState<JourneyType>(null);

  const handleLaunchApp = () => {
    if (
      window.location.hostname === "ithopu.com" ||
      window.location.hostname === "www.ithopu.com"
    ) {
      window.location.href = "https://app.ithopu.com";
    } else {
      window.location.href = "/dashboard";
    }
  };

  if (selectedJourney === null) {
    return (
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <Card
          className="border-4 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600 transition-all cursor-pointer hover:shadow-2xl group overflow-hidden relative"
          onClick={() => setSelectedJourney("parent")}
        >
          {/* Background Image with Overlay */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-opacity"
            style={{ backgroundImage: `url(${parentChildImg})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/90 to-white/95 dark:from-zinc-900/85 dark:via-zinc-900/90 dark:to-zinc-900/95 group-hover:from-white/80 group-hover:via-white/85 group-hover:to-white/90" />

          <CardContent className="pt-8 pb-8 text-center relative z-10">
            <h3 className="text-2xl font-bold mb-3 text-zinc-900 dark:text-white">
              For Parents
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 mb-4">
              Help your child become a mental math champion through guided
              practice and progress tracking.
            </p>
            <div className="flex items-center justify-center gap-1 mb-4">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm text-zinc-600 dark:text-zinc-400 ml-2">
                500+ parents
              </span>
            </div>
            <Button variant="outline" className="group-hover:bg-blue-50 dark:group-hover:bg-blue-950 bg-white/80 backdrop-blur-sm">
              Learn More
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </CardContent>
        </Card>

        <Card
          className="border-4 border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600 transition-all cursor-pointer hover:shadow-2xl group overflow-hidden relative"
          onClick={() => setSelectedJourney("teacher")}
        >
          {/* Background Image with Overlay */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-opacity"
            style={{ backgroundImage: `url(${teacherStudentImg})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/90 to-white/95 dark:from-zinc-900/85 dark:via-zinc-900/90 dark:to-zinc-900/95 group-hover:from-white/80 group-hover:via-white/85 group-hover:to-white/90" />

          <CardContent className="pt-8 pb-8 text-center relative z-10">
            <h3 className="text-2xl font-bold mb-3 text-zinc-900 dark:text-white">
              For Teachers
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 mb-4">
              Run engaging math classes with automated assignments, real-time
              analytics, and classroom management tools.
            </p>
            <div className="flex items-center justify-center gap-1 mb-4">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm text-zinc-600 dark:text-zinc-400 ml-2">
                100+ teachers
              </span>
            </div>
            <Button variant="outline" className="group-hover:bg-purple-50 dark:group-hover:bg-purple-950 bg-white/80 backdrop-blur-sm">
              Learn More
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedJourney === "parent") {
    return (
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => setSelectedJourney(null)}
          className="text-blue-600 dark:text-blue-400 hover:underline mb-6 flex items-center gap-2"
        >
          ← Back to selection
        </button>
        <Card className="border-4 border-blue-300 dark:border-blue-700 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-zinc-900">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-3xl font-bold mb-3 text-zinc-900 dark:text-white">
                Your Child's Journey to Math Mastery
              </h3>
              <p className="text-xl text-zinc-600 dark:text-zinc-400">
                See results in 2 weeks with daily 15-minute practice sessions
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-white mb-1">
                      Gamified Learning
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Earn badges, maintain streaks, battle friends, and climb
                      leaderboards—learning feels like playing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-white mb-1">
                      Parent Dashboard
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Track progress, monitor practice time, see mastery levels,
                      and receive weekly email reports.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-white mb-1">
                      Adaptive Difficulty
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      AI adjusts problem difficulty based on performance—always
                      challenging, never frustrating.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-white mb-1">
                      90+ Techniques
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      From Vedic Math to Olympiad strategies—step-by-step video
                      tutorials for every technique.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-white mb-1">
                      Daily Challenges
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Fresh problems every day to build consistency and maintain
                      practice streaks.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-white mb-1">
                      Olympiad Prep
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      MOEMS-style sample papers and competition strategies for
                      advanced learners.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 mb-6 border-2 border-zinc-200 dark:border-zinc-700">
              <h4 className="font-bold text-lg mb-4 text-zinc-900 dark:text-white">
                What Parents Say:
              </h4>
              <blockquote className="italic text-zinc-700 dark:text-zinc-300 mb-2">
                "My son went from taking 15 minutes on mental math homework to 2
                minutes in just 3 weeks. He actually asks to practice now!"
              </blockquote>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                — Sarah M., Parent of 4th grader
              </p>
            </div>

            <div className="text-center">
              <Button
                size="lg"
                onClick={handleLaunchApp}
                className="text-lg px-8 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Start Your Child's Journey Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-3">
                ✅ No credit card required • ✅ 30-second signup • ✅ 500+ happy families
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Teacher journey
  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => setSelectedJourney(null)}
        className="text-purple-600 dark:text-purple-400 hover:underline mb-6 flex items-center gap-2"
      >
        ← Back to selection
      </button>
      <Card className="border-4 border-purple-300 dark:border-purple-700 bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-zinc-900">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-3xl font-bold mb-3 text-zinc-900 dark:text-white">
              Run Engaging Math Classes, Not Boring Drills
            </h3>
            <p className="text-xl text-zinc-600 dark:text-zinc-400">
              Students practice at home, you see results in real-time
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                <GraduationCap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-bold mb-2 text-zinc-900 dark:text-white">
                Classroom Setup
              </h4>
              <ul className="text-sm text-zinc-600 dark:text-zinc-400 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Unlimited classrooms</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Unique join codes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Email invitations</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h4 className="font-bold mb-2 text-zinc-900 dark:text-white">
                Assignments
              </h4>
              <ul className="text-sm text-zinc-600 dark:text-zinc-400 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Create custom problem sets</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Auto-grading</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Due dates & penalties</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="w-6 h-6 text-pink-600 dark:text-pink-400" />
              </div>
              <h4 className="font-bold mb-2 text-zinc-900 dark:text-white">
                Analytics
              </h4>
              <ul className="text-sm text-zinc-600 dark:text-zinc-400 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Track 30 students instantly</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Identify struggling students</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Class performance trends</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 mb-6 border-2 border-zinc-200 dark:border-zinc-700">
            <h4 className="font-bold text-lg mb-4 text-zinc-900 dark:text-white">
              What Teachers Say:
            </h4>
            <blockquote className="italic text-zinc-700 dark:text-zinc-300 mb-2">
              "Ithopu transformed my classroom. Students compete for high scores
              on assignments, and I can see exactly who needs help. Setup took 5
              minutes."
            </blockquote>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              — Mr. Johnson, 5th Grade Math Teacher
            </p>
          </div>

          <div className="text-center">
            <Button
              size="lg"
              onClick={handleLaunchApp}
              className="text-lg px-8 py-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Setup Your Classroom Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-3">
              ✅ No credit card required • ✅ Unlimited students • ✅ 100+ teachers trust us
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
